import React, { useRef } from "react";
import { EndGunSection } from "../../dtos/pivot";
import "./PivotSetupGraphic.css";

// ── Dimensions ────────────────────────────────────────────────────
const SIZE = 280;
const CX = SIZE / 2;
const CY = SIZE / 2;
const PIVOT_R = 100;
const GUN_INNER = PIVOT_R + 7;
const GUN_OUTER = PIVOT_R + 26;
const HANDLE_R = 7;

// ── Helpers ───────────────────────────────────────────────────────

/** Normalize angle to [0, 360) */
function norm(a: number): number {
  return ((a % 360) + 360) % 360;
}

/** Angle (0 = North, clockwise) → SVG coordinate */
function pt(angle: number, r: number): { x: number; y: number } {
  const rad = (angle * Math.PI) / 180;
  return {
    x: CX + r * Math.sin(rad),
    y: CY - r * Math.cos(rad),
  };
}

function lineAtAngle(angle: number, radius: number, length: number): { x1: number; y1: number; x2: number; y2: number } {
  const center = pt(angle, radius);
  const perp = ((angle + 90) * Math.PI) / 180;
  const dx = Math.sin(perp) * (length / 2);
  const dy = -Math.cos(perp) * (length / 2);

  return {
    x1: center.x - dx,
    y1: center.y - dy,
    x2: center.x + dx,
    y2: center.y + dy,
  };
}

function arcLinePath(a1: number, a2: number, r: number, cw: boolean): string {
  const p1 = pt(a1, r);
  const p2 = pt(a2, r);
  const span = arcSpan(a1, a2, cw);
  const large = span > 180 ? 1 : 0;
  const sweep = cw ? 1 : 0;
  return `M ${f(p1.x)} ${f(p1.y)} A ${r} ${r} 0 ${large} ${sweep} ${f(p2.x)} ${f(p2.y)}`;
}

/** SVG coordinate → angle (0 = North, clockwise) */
function toDeg(x: number, y: number): number {
  return norm((Math.atan2(x - CX, CY - y) * 180) / Math.PI);
}

/** Angular span from a1 → a2 in given direction */
function arcSpan(a1: number, a2: number, cw: boolean): number {
  return cw ? norm(a2 - a1) : norm(a1 - a2);
}

/** Pie-slice sector from the center point */
function sectorPath(a1: number, a2: number, r: number, cw: boolean): string {
  const s = arcSpan(a1, a2, cw);
  if (s === 0) return "";
  const p1 = pt(a1, r);
  const p2 = pt(a2, r);
  const large = s > 180 ? 1 : 0;
  const sweep = cw ? 1 : 0;
  return `M ${CX} ${CY} L ${f(p1.x)} ${f(p1.y)} A ${r} ${r} 0 ${large} ${sweep} ${f(p2.x)} ${f(p2.y)} Z`;
}

/** Annular (ring) arc segment */
function ringPath(a1: number, a2: number, ir: number, or_: number, cw: boolean): string {
  const s = arcSpan(a1, a2, cw);
  if (s === 0) return "";
  const i1 = pt(a1, ir), o1 = pt(a1, or_);
  const i2 = pt(a2, ir), o2 = pt(a2, or_);
  const sweep = cw ? 1 : 0;
  const large = s > 180 ? 1 : 0;
  return [
    `M ${f(i1.x)} ${f(i1.y)}`,
    `L ${f(o1.x)} ${f(o1.y)}`,
    `A ${or_} ${or_} 0 ${large} ${sweep} ${f(o2.x)} ${f(o2.y)}`,
    `L ${f(i2.x)} ${f(i2.y)}`,
    `A ${ir} ${ir} 0 ${large} ${1 - sweep} ${f(i1.x)} ${f(i1.y)}`,
    "Z",
  ].join(" ");
}

/** Round to 2 decimal places for clean SVG output */
function f(n: number): string {
  return n.toFixed(2);
}

// ── Props ─────────────────────────────────────────────────────────

export interface PivotSetupGraphicProps {
  limit_angle_a: number;
  limit_angle_b: number;
  travel_direction: "CW" | "CCW";
  end_gun_enabled: boolean;
  end_gun_sections: EndGunSection[];
  current_angle?: number;
  onLimitAngleAChange?: (angle: number) => void;
  onLimitAngleBChange?: (angle: number) => void;
  onEndGunSectionChange?: (index: number, key: "limit_angle_1" | "limit_angle_2", angle: number) => void;
  onEndGunSectionToggle?: (index: number, enabled: boolean) => void;
}

// ── Drag state type ───────────────────────────────────────────────

type DragTarget =
  | { kind: "a" }
  | { kind: "b" }
  | { kind: "sec"; i: number; which: 1 | 2 };

// ── Component ─────────────────────────────────────────────────────

const COMPASS = [
  { label: "N", angle: 0 },
  { label: "E", angle: 90 },
  { label: "S", angle: 180 },
  { label: "W", angle: 270 },
];

export const PivotSetupGraphic: React.FC<PivotSetupGraphicProps> = ({
  limit_angle_a,
  limit_angle_b,
  travel_direction,
  end_gun_enabled,
  end_gun_sections,
  current_angle,
  onLimitAngleAChange,
  onLimitAngleBChange,
  onEndGunSectionChange,
  onEndGunSectionToggle,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<DragTarget | null>(null);

  const cw = travel_direction === "CW";
  const a = norm(limit_angle_a);
  const b = norm(limit_angle_b);
  const liveAngle = current_angle === undefined ? undefined : norm(current_angle);

  // Convert client mouse coords → SVG-space coords (handles scaling)
  const clientToSvg = (clientX: number, clientY: number) => {
    const rect = svgRef.current!.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * SIZE,
      y: ((clientY - rect.top) / rect.height) * SIZE,
    };
  };

  const startDrag = (target: DragTarget) => (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = target;
  };

  const onMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!dragRef.current) return;
    const { x, y } = clientToSvg(e.clientX, e.clientY);
    const angle = Math.round(toDeg(x, y));
    const d = dragRef.current;
    if (d.kind === "a") onLimitAngleAChange?.(angle);
    else if (d.kind === "b") onLimitAngleBChange?.(angle);
    else onEndGunSectionChange?.(d.i, d.which === 1 ? "limit_angle_1" : "limit_angle_2", angle);
  };

  const stopDrag = () => { dragRef.current = null; };

  const ptA = pt(a, PIVOT_R);
  const ptB = pt(b, PIVOT_R);
  const livePoint = liveAngle === undefined ? null : pt(liveAngle, GUN_OUTER);
  const liveTowerBars = liveAngle === undefined
    ? []
    : [0.28, 0.52, 0.76].map(ratio => lineAtAngle(liveAngle, PIVOT_R * ratio, 12));
  const liveEndBar = liveAngle === undefined ? null : lineAtAngle(liveAngle, GUN_OUTER - 4, 14);
  const liveEndTicks = liveAngle === undefined
    ? []
    : [GUN_OUTER - 1, GUN_OUTER + 7].map(radius => lineAtAngle(liveAngle, radius, 5));

  // Position angle label slightly outside the handle
  const labelOffset = (p: { x: number; y: number }) => ({
    x: p.x + (p.x > CX ? 11 : -11),
    y: p.y + (p.y > CY ? 4 : -4),
    anchor: p.x > CX ? ("start" as const) : ("end" as const),
  });

  const labelA = labelOffset(ptA);
  const labelB = labelOffset(ptB);
  const liveLabel = livePoint ? {
    x: livePoint.x + (livePoint.x >= CX ? 10 : -10),
    y: livePoint.y + (livePoint.y >= CY ? 12 : -12),
    anchor: livePoint.x >= CX ? ("start" as const) : ("end" as const),
  } : null;
  const directionArrowBaseAngle = (liveAngle ?? (cw ? 45 : 315)) + (cw ? 18 : -18);
  const directionArrowPath = arcLinePath(
    cw ? directionArrowBaseAngle - 12 : directionArrowBaseAngle + 12,
    cw ? directionArrowBaseAngle + 12 : directionArrowBaseAngle - 12,
    GUN_OUTER + 10,
    cw
  );
  const endGunLabelStart = pt(42, GUN_OUTER + 16);
  const endGunLabelEnd = pt(84, GUN_OUTER + 16);
  const endGunLabelPath = `M ${f(endGunLabelStart.x)} ${f(endGunLabelStart.y)} A ${GUN_OUTER + 16} ${GUN_OUTER + 16} 0 0 1 ${f(endGunLabelEnd.x)} ${f(endGunLabelEnd.y)}`;

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className="pivot-graphic"
      onMouseMove={onMouseMove}
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
    >
      <defs>
        <marker
          id="pivot-direction-arrowhead"
          markerWidth="6"
          markerHeight="6"
          refX="4.75"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M 0 0 L 6 3 L 0 6 z" className="pivot-direction-arrowhead" />
        </marker>
      </defs>

      {/* Compass labels */}
      {COMPASS.map(({ label, angle }) => {
        const p = pt(angle, PIVOT_R + 20);
        return (
          <text key={label} x={p.x} y={p.y} className="pivot-compass"
            dominantBaseline="middle" textAnchor="middle">
            {label}
          </text>
        );
      })}

      {/* Pivot travel circle */}
      <circle cx={CX} cy={CY} r={PIVOT_R} className="pivot-circle" />
      <path
        d={directionArrowPath}
        className="pivot-direction-arrow"
        markerEnd="url(#pivot-direction-arrowhead)"
      />

      {/* Operating arc */}
      <path d={sectorPath(a, b, PIVOT_R, cw)} className="pivot-arc" />

      {/* End gun ring segments */}
      {end_gun_enabled && end_gun_sections.map((sec, i) => (
        <path
          key={i}
          d={ringPath(sec.limit_angle_1, sec.limit_angle_2, GUN_INNER, GUN_OUTER, true)}
          className={sec.enabled ? "gun-section-on" : "gun-section-off"}
          onClick={() => onEndGunSectionToggle?.(i, !sec.enabled)}
        />
      ))}

      {/* Limit lines */}
      <line x1={CX} y1={CY} x2={ptA.x} y2={ptA.y} className="limit-line-a" />
      <line x1={CX} y1={CY} x2={ptB.x} y2={ptB.y} className="limit-line-b" />

      {/* Current angle marker */}
      {livePoint && (
        <>
          <line x1={CX} y1={CY} x2={livePoint.x} y2={livePoint.y} className="current-angle-line-shadow" />
          <line x1={CX} y1={CY} x2={livePoint.x} y2={livePoint.y} className="current-angle-line" />
          {liveTowerBars.map((towerBar, index) => (
            <line
              key={`tower-${index}`}
              x1={towerBar.x1}
              y1={towerBar.y1}
              x2={towerBar.x2}
              y2={towerBar.y2}
              className="current-angle-crossbar"
            />
          ))}
          {liveEndBar && (
            <line
              x1={liveEndBar.x1}
              y1={liveEndBar.y1}
              x2={liveEndBar.x2}
              y2={liveEndBar.y2}
              className="current-angle-crossbar current-angle-crossbar-end"
            />
          )}
          {liveEndTicks.map((tick, index) => (
            <line
              key={`tick-${index}`}
              x1={tick.x1}
              y1={tick.y1}
              x2={tick.x2}
              y2={tick.y2}
              className="current-angle-end-tick"
            />
          ))}
          {liveLabel && (
            <text
              x={liveLabel.x}
              y={liveLabel.y}
              className="current-angle-label"
              textAnchor={liveLabel.anchor}
            >
              {Math.round(liveAngle ?? 0)}°
            </text>
          )}
        </>
      )}

      {/* Center dot */}
      <circle cx={CX} cy={CY} r={4} className="pivot-center" />

      {/* Limit A handle + label */}
      <circle cx={ptA.x} cy={ptA.y} r={HANDLE_R} className="handle handle-a"
        onMouseDown={startDrag({ kind: "a" })} />
      <text x={ptA.x} y={ptA.y} className="handle-label"
        dominantBaseline="middle" textAnchor="middle">A</text>
      <text x={labelA.x} y={labelA.y} className="angle-label" textAnchor={labelA.anchor}>
        {Math.round(a)}°
      </text>

      {/* Limit B handle + label */}
      <circle cx={ptB.x} cy={ptB.y} r={HANDLE_R} className="handle handle-b"
        onMouseDown={startDrag({ kind: "b" })} />
      <text x={ptB.x} y={ptB.y} className="handle-label"
        dominantBaseline="middle" textAnchor="middle">B</text>
      <text x={labelB.x} y={labelB.y} className="angle-label" textAnchor={labelB.anchor}>
        {Math.round(b)}°
      </text>

      {/* End gun section handles (drag to reposition section angles) */}
      {end_gun_enabled && end_gun_sections.map((sec, i) => {
        const p1 = pt(sec.limit_angle_1, GUN_OUTER);
        const p2 = pt(sec.limit_angle_2, GUN_OUTER);
        return (
          <g key={i}>
            <circle cx={p1.x} cy={p1.y} r={5} className="handle handle-gun"
              onMouseDown={startDrag({ kind: "sec", i, which: 1 })} />
            <circle cx={p2.x} cy={p2.y} r={5} className="handle handle-gun"
              onMouseDown={startDrag({ kind: "sec", i, which: 2 })} />
          </g>
        );
      })}

      {/* <path id="endgun-label-path" d={endGunLabelPath} className="endgun-callout" />
      <text className="endgun-label">
        <textPath href="#endgun-label-path" startOffset="40%" textAnchor="middle">
          Endgun
        </textPath>
      </text> */}
    </svg>
  );
};
